/**
 * KanbanCard — Componente independiente para tarjetas Kanban (0259)
 * Punto 3: extracción. Punto 4: AppointmentCard (servicio, fecha, profesional).
 * Punto 6: TicketCard para tickets sin cita (nombre, teléfono, último mensaje).
 * Variantes: hasAppointment → AppointmentCardContent; sino → TicketCardContent.
 */
import React from "react";
import { format, parseISO } from "date-fns";
import { es } from "date-fns/locale";
import { makeStyles } from "@material-ui/core/styles";
import { Card, CardContent, Avatar, IconButton, Typography, Chip } from "@material-ui/core";
import { Chat as ChatIcon, Schedule as ScheduleIcon, DragIndicator as DragIcon, Event as EventIcon } from "@material-ui/icons";
import { useTheme } from "@material-ui/core/styles";
import { getInitials } from "../../helpers/getInitials";
import { generateColor } from "../../helpers/colorGenerator";
import { getProfilePicUrl } from "../../services/api";
// `softTone` deriva un par fondo/texto legible a partir de un color de marca, según el
// tema activo. Vive en agendaTheme por su origen, pero es una utilidad de color genérica.
import { softTone } from "../../theme/agendaTheme";
import { i18n } from "../../translate/i18n";

function formatAppointmentDate(date) {
  if (!date) return "";
  try {
    const d = typeof date === "string" ? parseISO(date) : new Date(date);
    return format(d, "d MMM - h a", { locale: es });
  } catch {
    return String(date);
  }
}

// 0259 UX: Maqueta compacta — fuentes pequeñas, tarjetas sutiles
const useStyles = makeStyles((theme) => ({
  card: {
    marginBottom: theme.spacing(1),
    cursor: "grab",
    transition: "all 0.2s ease",
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 4,
    backgroundColor: theme.palette.background.paper,
    boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
    "&:hover": {
      transform: "translateY(-1px)",
      boxShadow: "0 2px 6px rgba(0,0,0,0.12)",
      borderColor: theme.palette.primary.light,
    },
    "&:active": {
      cursor: "grabbing",
    },
  },
  cardContent: {
    padding: theme.spacing(1),
    "&:last-child": {
      paddingBottom: theme.spacing(1),
    },
  },
  cardHeader: {
    display: "flex",
    alignItems: "center",
    marginBottom: theme.spacing(0.5),
    gap: theme.spacing(0.5),
  },
  cardHeaderLeft: {
    display: "flex",
    alignItems: "center",
    flex: 1,
    minWidth: 0,
    gap: theme.spacing(0.5),
  },
  cardHeaderIcons: {
    display: "flex",
    alignItems: "center",
    gap: 2,
    flexShrink: 0,
  },
  avatar: {
    width: 28,
    height: 28,
    marginRight: theme.spacing(0.75),
    fontSize: "0.75rem",
    backgroundColor: theme.palette.primary.main,
    border: `1px solid ${theme.palette.divider}`,
  },
  contactInfo: {
    flex: 1,
    minWidth: 0,
  },
  contactName: {
    fontWeight: 600,
    fontSize: "0.8rem",
    lineHeight: 1.3,
    marginBottom: 1,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  contactNumber: {
    fontSize: "0.7rem",
    color: theme.palette.text.secondary,
    lineHeight: 1.2,
  },
  lastMessage: {
    fontSize: "0.75rem",
    color: theme.palette.text.secondary,
    lineHeight: 1.3,
    marginTop: theme.spacing(0.25),
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    maxWidth: "100%",
  },
  // 0261 maqueta: serviceRow con icons alineados a la derecha
  serviceRowWithIcons: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(0.75),
    marginBottom: theme.spacing(0.25),
    flexWrap: "wrap",
  },
  serviceDateGroup: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.75),
    flexWrap: "wrap",
    flex: 1,
    minWidth: 0,
  },
  quickActions: {
    display: "flex",
    alignItems: "center",
    gap: 2,
    flexShrink: 0,
  },
  actionButton: {
    padding: 2,
    minWidth: 28,
    height: 28,
    "& .MuiSvgIcon-root": { fontSize: "0.9rem" },
    "&:hover": {
      backgroundColor: theme.palette.action.hover,
    },
  },
  dragIcon: {
    color: theme.palette.text.disabled,
    fontSize: "0.85rem",
    marginRight: theme.spacing(0.5),
  },
  // 0259 Punto 4: AppointmentCard (maqueta compacta)
  serviceRow: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.75),
    marginBottom: theme.spacing(0.25),
    flexWrap: "wrap",
  },
  serviceName: {
    fontWeight: 600,
    fontSize: "0.8rem",
    color: theme.palette.text.primary,
    lineHeight: 1.3,
  },
  dateRow: {
    display: "flex",
    alignItems: "center",
    gap: 4,
    fontSize: "0.75rem",
    color: theme.palette.text.secondary,
  },
  professionalChip: {
    height: 22,
    fontSize: "0.7rem",
    "& .MuiChip-label": { padding: "0 6px" },
  },
  professionalChipRow: {
    display: "flex",
    justifyContent: "flex-end",
    marginTop: theme.spacing(0.5),
  },
  /**
   * 0391 — Pie de tarjeta: quién ATIENDE y en qué departamento.
   *
   * No confundir con el chip de profesional: ese es quien da la sesión. Aquí va el
   * operador que lleva la conversación, que es lo que un responsable necesita ver de un
   * vistazo —sobre todo para detectar lo que no está atendiendo nadie.
   *
   * Va separado por una línea fina y en tamaño menor: informa sin competir con el
   * contacto ni con los datos de la cita.
   */
  ownerRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(0.5),
    marginTop: theme.spacing(0.75),
    paddingTop: theme.spacing(0.5),
    borderTop: `1px solid ${theme.palette.divider}`,
    minWidth: 0,
  },
  ownerName: {
    display: "flex",
    alignItems: "center",
    gap: 4,
    fontSize: "0.7rem",
    color: theme.palette.text.secondary,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    minWidth: 0,
  },
  ownerAvatar: {
    width: 16,
    height: 16,
    fontSize: "0.55rem",
    fontWeight: 700,
  },
  /** Sin dueño: el dato más accionable del tablero, por eso se resalta. */
  ownerUnassigned: {
    fontStyle: "italic",
    color: theme.palette.warning.main,
  },
  queueChip: {
    height: 18,
    fontSize: "0.65rem",
    maxWidth: 110,
    flexShrink: 0,
    "& .MuiChip-label": { padding: "0 6px" },
  },
}));

function truncateText(text, maxLength = 50) {
  if (!text) return "";
  return text.length > maxLength ? text.substring(0, maxLength) + "..." : text;
}

/**
 * Pie con el responsable de la conversación y su departamento.
 *
 * Es información distinta del profesional de la cita: aquí se responde "¿quién está
 * atendiendo esto?", que es lo que permite repartir trabajo y detectar lo que lleva rato
 * sin dueño.
 */
function CardOwnerRow({ user, queue, aiAgent, classes, mode }) {
  const assigned = Boolean(user?.name);
  /**
   * Un ticket atendido por el agente NO tiene usuario, y no por descuido: el servidor
   * solo invoca a la IA cuando no hay persona asignada. Mostrarlo como «Sin asignar»
   * hacía que un supervisor lo leyera como trabajo por repartir, cuando en realidad
   * está atendido y no hay que tocarlo. El servidor ya resuelve quién atiende.
   */
  const porAgente = !assigned && Boolean(aiAgent?.name);

  /**
   * Los colores de cola y de avatar son de marca y se eligen sin pensar en el tema: la
   * cola "Contabilidad" es `#5300eb`, un morado casi negro. Pintado en crudo sobre
   * superficie oscura resulta ilegible. `softTone` deriva un par fondo/texto con
   * contraste suficiente en ambos temas, conservando el matiz que identifica a la cola.
   */
  const queueTone = queue?.name ? softTone(queue.color || "#9e9e9e", mode) : null;
  const avatarTone = assigned
    ? softTone(generateColor(String(user.id ?? user.name)), mode)
    : null;

  return (
    <div className={classes.ownerRow}>
      <span
        className={`${classes.ownerName} ${assigned || porAgente ? "" : classes.ownerUnassigned}`}
        title={
          assigned
            ? user.name
            : porAgente
            ? i18n.t("kanban.attendedByAI", { name: aiAgent.name })
            : i18n.t("kanban.unassignedOwner")
        }
      >
        {assigned ? (
          <>
            <Avatar
              className={classes.ownerAvatar}
              style={{
                backgroundColor: avatarTone.backgroundColor,
                color: avatarTone.color,
              }}
            >
              {getInitials(user.name) || "?"}
            </Avatar>
            {truncateText(user.name, 16)}
          </>
        ) : porAgente ? (
          <>
            {/* Icono en vez de iniciales: a un vistazo hay que poder separar las tres
                situaciones —persona, agente y sin dueño— sin leer el texto. */}
            <span className={classes.ownerAvatar} role="img" aria-label="agente">
              🤖
            </span>
            {truncateText(aiAgent.name, 18)}
          </>
        ) : (
          i18n.t("kanban.unassignedOwner")
        )}
      </span>
      {queue?.name && (
        <Chip
          size="small"
          label={truncateText(queue.name, 14)}
          className={classes.queueChip}
          style={{
            backgroundColor: queueTone.backgroundColor,
            color: queueTone.color,
            border: `1px solid ${queueTone.borderColor}`,
          }}
        />
      )}
    </div>
  );
}

function getContactAvatar(contact) {
  if (!contact) return null;
  if (
    contact.profilePicUrl &&
    contact.profilePicUrl !== "" &&
    !contact.profilePicUrl.includes("nopicture.png")
  ) {
    return getProfilePicUrl(contact.profilePicUrl);
  }
  return null;
}

/** 0261 maqueta: TicketCard con icons a la derecha. */
function TicketCardContent({ ticket, classes, actionIcons }) {
  return (
    <div className={classes.serviceRowWithIcons}>
      <Typography variant="body2" className={classes.lastMessage} style={{ flex: 1, minWidth: 0 }}>
        {truncateText(ticket.lastMessage, 50)}
      </Typography>
      {actionIcons}
    </div>
  );
}

/** 0261 maqueta PROPUESTA: servicio + fecha en línea, icons (reloj, chat) alineados a la derecha. */
function AppointmentCardContent({ serviceName, appointmentDate, professionalName, classes, actionIcons }) {
  return (
    <>
      <div className={classes.serviceRowWithIcons}>
        <div className={classes.serviceDateGroup}>
          {serviceName && (
            <Typography variant="body2" className={classes.serviceName}>
              {serviceName}
            </Typography>
          )}
          {appointmentDate && (
            <div className={classes.dateRow}>
              <EventIcon fontSize="small" />
              {formatAppointmentDate(appointmentDate)}
            </div>
          )}
        </div>
        {actionIcons}
      </div>
      {professionalName && (
        <div className={classes.professionalChipRow}>
          <Chip
            label={professionalName}
            size="small"
            color="primary"
            variant="outlined"
            className={classes.professionalChip}
          />
        </div>
      )}
    </>
  );
}

export default function KanbanCard({
  ticket,
  isMoving,
  isDragging,
  onCardClick,
  onCalendarClick,
}) {
  const classes = useStyles();
  const theme = useTheme();
  const mode = theme.palette.type === "dark" ? "dark" : "light";

  const hasAppointment =
    ticket.currentAppointmentId ||
    (ticket.appointment && (ticket.appointment.serviceName || ticket.appointment.appointmentDate || ticket.appointment.professionalName));

  const serviceName = ticket.appointment?.serviceName ?? ticket.serviceName ?? null;
  const appointmentDate = ticket.appointment?.appointmentDate ?? ticket.appointmentDate ?? null;
  const professionalName = ticket.appointment?.professionalName ?? ticket.professionalName ?? null;

  const handleAvatarError = (event) => {
    event.target.style.display = "none";
  };

  return (
    <Card
      className={classes.card}
      style={{
        transform: isDragging ? "rotate(5deg)" : "none",
        boxShadow: isDragging ? "0 5px 10px rgba(0,0,0,0.15)" : "none",
        opacity: isMoving ? 0.6 : 1,
        pointerEvents: isMoving ? "none" : "auto",
        transition: "all 0.3s ease",
        border: isDragging ? "2px solid #1976d2" : undefined,
      }}
    >
      <CardContent className={classes.cardContent}>
        {/* 0261 maqueta: Header compacto — drag, avatar, nombre */}
        <div className={classes.cardHeader}>
          <div className={classes.cardHeaderLeft}>
            <DragIcon className={classes.dragIcon} />
            <Avatar
              className={classes.avatar}
              src={getContactAvatar(ticket.contact)}
              onError={handleAvatarError}
              style={{
                backgroundColor: generateColor(ticket.contact?.number),
                color: "white",
                fontWeight: "bold",
              }}
            >
              {getInitials(ticket.contact?.name) || "?"}
            </Avatar>
            <div className={classes.contactInfo}>
              <Typography variant="body2" className={classes.contactName}>
                {ticket.contact?.name || "Sin contacto"}
              </Typography>
              {!hasAppointment && (
                <Typography variant="caption" className={classes.contactNumber}>
                  {ticket.contact?.number || "N/A"}
                </Typography>
              )}
            </div>
          </div>
        </div>

        {/* 0261 maqueta PROPUESTA: servicio/fecha + icons (reloj→Calendario, chat) alineados a la derecha */}
        {(() => {
          const actionIcons = (
            <div className={classes.quickActions}>
              <IconButton
                className={classes.actionButton}
                onClick={(e) => {
                  e.stopPropagation();
                  onCalendarClick?.(e);
                }}
                title="Ver calendario Agenda"
              >
                <ScheduleIcon fontSize="small" />
              </IconButton>
              <IconButton
                className={classes.actionButton}
                onClick={(e) => {
                  e.stopPropagation();
                  onCardClick?.(ticket);
                }}
                title="Ver conversación"
              >
                <ChatIcon fontSize="small" />
              </IconButton>
            </div>
          );
          return hasAppointment ? (
            <AppointmentCardContent
              serviceName={serviceName}
              appointmentDate={appointmentDate}
              professionalName={professionalName}
              classes={classes}
              actionIcons={actionIcons}
            />
          ) : (
            <TicketCardContent ticket={ticket} classes={classes} actionIcons={actionIcons} />
          );
        })()}

        {/* 0391: quién atiende y en qué departamento — común a citas y a tickets sueltos */}
        <CardOwnerRow user={ticket.user} queue={ticket.queue} aiAgent={ticket.aiAgent} classes={classes} mode={mode} />
      </CardContent>
    </Card>
  );
}
